/**
 * Money, quantity and percent parsing for the healthcare rate screens.
 *
 * Rules: what a person types is parsed from TEXT into an INTEGER (cents, or hundredths of a percent) with string operations and integer
 * arithmetic only, never `parseFloat(...) * 100`. A value reaches the wire by building a decimal string from the integer and parsing that,
 * so no binary floating-point multiplication or division ever touches an amount. Amounts the server sends are shown as received.
 * The mock server's own pricing (mockRateResolver.ts) uses BigInt over integer units for the same reason.
 */

const WHOLE_AND_FRACTION = /^(\d{1,12})(?:\.(\d{1,}))?$/;

/** Strips a leading $ and thousands commas; returns null when what is left is not a plain non-negative decimal. */
function cleanDecimalText(raw: string): string | null {
  const t = raw.trim().replace(/^\$/, '').replace(/,(?=\d{3}(\D|$))/g, '');
  return WHOLE_AND_FRACTION.test(t) ? t : null;
}

/**
 * Text to an integer in 1/100 units (cents, or hundredths of a percent), or null when it is not a non-negative number with at most 2 decimals
 * or is above `maxWhole`. "12" is 1200, "12.5" is 1250, "12.50" is 1250, "$1,234.56" is 123456.
 */
export function parseScaled2(raw: string, maxWhole: number): number | null {
  const t = cleanDecimalText(raw);
  if (t === null) return null;
  const [whole = '0', fraction = ''] = t.split('.');
  if (fraction.length > 2) return null;
  const units = Number(whole) * 100 + Number(fraction.padEnd(2, '0'));
  return units > maxWhole * 100 ? null : units;
}

/** A rate or amount in dollars with at most 2 decimals, as integer cents (the server accepts up to 1,000,000). */
export const parseCents = (raw: string): number | null => parseScaled2(raw, 1_000_000);

/** A percentage 0..1000 with at most 2 decimals, as integer hundredths of a percent. */
export const parsePercentHundredths = (raw: string): number | null => parseScaled2(raw, 1000);

/** An integer in 1/100 units as a JSON number, built from a decimal string (123456 -> 1234.56). Exact for every value the UI can produce. */
export function scaled2ToWire(units: number): number {
  const whole = Math.trunc(units / 100);
  const fraction = String(units % 100).padStart(2, '0');
  return Number(`${whole}.${fraction}`);
}

export const centsToWire = scaled2ToWire;

/** "$1,234.56" from integer cents (the integer part is formatted by the browser, the cents are appended as digits). */
export function formatCents(cents: number): string {
  const whole = Math.trunc(cents / 100);
  return `$${whole.toLocaleString('en-US')}.${String(cents % 100).padStart(2, '0')}`;
}

/** A server amount for display: at least 2 and at most 4 decimals, so a 4-decimal rate is never silently rounded on screen. */
export function formatWireMoney(amount: number | null | undefined): string {
  if (amount === null || amount === undefined || !Number.isFinite(amount)) return '—';
  return amount.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 4 });
}

/** Cents back to the text a person would type ("1250" -> "12.50"), for an edit form. */
export function centsToInputText(cents: number): string {
  return `${Math.trunc(cents / 100)}.${String(cents % 100).padStart(2, '0')}`;
}

/** Quantity: greater than zero, at most 10,000, at most 4 decimals. The result is `Number(text)`: a parse, not arithmetic. */
export function parseQuantity(raw: string): number | null {
  const t = cleanDecimalText(raw);
  if (t === null) return null;
  const [whole = '0', fraction = ''] = t.split('.');
  if (fraction.length > 4) return null;
  const value = Number(t);
  if (!(value > 0) || Number(whole) > 10_000 || value > 10_000) return null;
  return value;
}

/** Worked hours: 0..744 with at most 2 decimals. */
export function parseWorkedHours(raw: string): number | null {
  const t = cleanDecimalText(raw);
  if (t === null) return null;
  const [, fraction = ''] = t.split('.');
  if (fraction.length > 2) return null;
  const value = Number(t);
  return value <= 744 ? value : null;
}

/** A quantity or hours figure for display ("3", "1.5"): trailing zeros are never added. */
export function formatQuantity(value: number): string {
  return value.toLocaleString('en-US', { maximumFractionDigits: 4 });
}

// ---- exact integer helpers used by the mock server's pricing ----

/** A decimal with at most 4 decimals to integer units of 1/10,000. Goes through text so no float multiplication happens. */
export function toUnits4(value: number): bigint {
  const text = value.toFixed(4);
  const negative = text.startsWith('-');
  const [whole = '0', fraction = '0000'] = text.replace('-', '').split('.');
  const units = BigInt(whole) * 10000n + BigInt(fraction);
  return negative ? -units : units;
}

/** Integer units of 1/10,000 dollars as money with at least 2 and at most 4 decimals ("$85.00", "$12.3456"), without going through a float. */
export function formatUnits4Money(units: bigint): string {
  const negative = units < 0n;
  const abs = negative ? -units : units;
  const whole = (abs / 10000n).toLocaleString('en-US');
  let fraction = String(abs % 10000n).padStart(4, '0');
  while (fraction.length > 2 && fraction.endsWith('0')) fraction = fraction.slice(0, -1);
  return `${negative ? '-' : ''}$${whole}.${fraction}`;
}

/** Integer units of 1/10,000 back to a JSON number (built from a decimal string). */
export function fromUnits4(units: bigint): number {
  const negative = units < 0n;
  const abs = negative ? -units : units;
  const text = `${abs / 10000n}.${String(abs % 10000n).padStart(4, '0')}`;
  return Number(negative ? `-${text}` : text);
}

/** Round half away from zero of `numerator / denominator` for non-negative values. */
export function divRound(numerator: bigint, denominator: bigint): bigint {
  return (numerator + denominator / 2n) / denominator;
}

/** Integer cents to a JSON number, for BigInt callers. */
export function bigCentsToWire(cents: bigint): number {
  return scaled2ToWire(Number(cents));
}
